import React, { Suspense, useMemo, useState } from "react";
import { inject, observer } from "mobx-react";
import { withResizeDetector } from "react-resize-detector";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { UserMenu } from "./partials";
import Header from "./partials/Header";

interface ILayout {
  exact: boolean;
  computedMatch: any;
  component: any;
  icon?: string;
  title: string;
  UIStore?: any;
  width: number;
  height: number;
  targetRef?: any;
  routes?: any;
}

const Layout = inject("UIStore")(
  observer(
    withResizeDetector((props: ILayout) => {
      const {
        exact,
        computedMatch,
        component: Component,
        icon,
        title,
        UIStore,
        width,
        height,
        targetRef,
        routes,
        ...rest
      } = props;
      const {
        colorScheme,
        enableFixedHeader,
        enableFixedSidebar,
        enableFixedFooter,
        enableClosedSidebar,
        closedSmallerSidebar,
        enableMobileMenu,
        enablePageTabsAlt,
      } = UIStore;
      const [fullScreen, setFullScreen] = useState(enableClosedSidebar);
      const { t } = useTranslation();
      const MemoizedComponent = useMemo(() => <Component {...rest} />, []);

      return useMemo(() => {
        return (
          <div
            ref={targetRef}
            className={cx(
              "app-container app-theme-" + colorScheme,
              { "fixed-header": enableFixedHeader },
              { "fixed-sidebar": enableFixedSidebar || width < 1250 },
              { "fixed-footer": enableFixedFooter },
              {
                "closed-sidebar":
                  fullScreen || enableClosedSidebar || width < 1250,
              },
              {
                "closed-sidebar-mobile": closedSmallerSidebar || width < 1250,
              },
              { "sidebar-mobile-open": enableMobileMenu },
              { "body-tabs-shadow-btn": enablePageTabsAlt }
            )}
          >
            <Header />
            <div className="app-main">
              <div className="app-main__outer">
                <div className="app-main__inner p-0">
                  <Suspense
                    fallback={
                      <div className="loader-container">
                        <div className="loader-container-inner">
                          <div className="text-center"></div>
                          <h6 className="mt-3">
                            Please wait while we load the page...
                            <small>
                              Because we load at once all the contents.
                            </small>
                          </h6>
                        </div>
                      </div>
                    }
                  >
                    <div className="app-inner-layout">
                      <div className="app-inner-layout__container">
                        {!(closedSmallerSidebar || width < 1250)}

                        <div className="">{MemoizedComponent}</div>
                      </div>
                    </div>
                  </Suspense>
                </div>
              </div>
            </div>
          </div>
        );
      }, [
        targetRef,
        colorScheme,
        enableFixedHeader,
        enableFixedSidebar,
        width,
        enableFixedFooter,
        fullScreen,
        enableClosedSidebar,
        closedSmallerSidebar,
        enableMobileMenu,
        enablePageTabsAlt,
        MemoizedComponent,
      ]);
    })
  )
);
const InframeLayout = (props: ILayout) => {
  const {
    exact,
    computedMatch,
    component: Component,
    icon,
    title,
    targetRef,
    ...rest
  } = props;

  const MemoizedComponent = useMemo(() => <Component {...rest} />, []);

  return useMemo(
    () => (
      <div ref={targetRef} className="app-container app-theme-white">
        <div className="app-main">
          <div className="app-main__outer">
            <div className="app-main__inner p-0">
              <Suspense
                fallback={
                  <div className="loader-container">
                    <div className="loader-container-inner">
                      <div className="text-center"></div>
                      <h6 className="mt-3">
                        Please wait while we load the page...
                        <small>Because we load at once all the contents.</small>
                      </h6>
                    </div>
                  </div>
                }
              >
                <div className="app-inner-layout">
                  <div className="app-inner-layout__container p-3 p-lg-5">
                    <div className="card main-card mb-3">
                      {MemoizedComponent}
                    </div>
                  </div>
                </div>
              </Suspense>
            </div>
          </div>
        </div>
      </div>
    ),
    [MemoizedComponent, targetRef]
  );
};

export { InframeLayout as Iframe };

export default Layout;
